/**
 * src/primate/telemetry-bridge.ts — host-side Telemetry implementation
 * (PRI-439 R14, design §26-R14).
 *
 * Bridges the core telemetry seam (src/core/telemetry) to the first-party
 * stacks: analytics events → src/primate/analytics (GA4 + PostHog), error
 * reporting → @sentry/react. src/core stays dependency-clean; this module is
 * app-plane and never enters the published package.
 *
 * Dispatch is by the SAME snake_case event names the analytics module uses,
 * so event payloads/dashboards are unchanged by the R14 refactor.
 */
import * as Sentry from '@sentry/react'
import type { Telemetry, TelemetryTracker, TelemetryErrorReporter } from '../core/telemetry/TelemetryProvider'
import * as analytics from './analytics/analytics'

// ── Tracker: dispatch core events to the existing typed analytics fns ────────

/* eslint-disable @typescript-eslint/no-explicit-any */
const EVENT_DISPATCH: Record<string, (props: any) => void> = {
  video_uploaded: analytics.trackVideoUploaded,
  video_analysed: analytics.trackVideoAnalysed,
  analysis_result: analytics.trackAnalysisResult,
  analysis_cancelled: (p) => analytics.trackAnalysisCancelled(p?.user_plan ?? 'unknown'),
  user_video_saved: analytics.trackUserVideoSaved,
  video_renamed: analytics.trackVideoRenamed,
  demo_quota_hit: () => analytics.trackDemoQuotaHit(),
  query_parsed: analytics.trackQueryParsed,
  query_parse_failed: analytics.trackQueryParseFailed,
  compiled_query_edited: analytics.trackCompiledQueryEdited,
  batch_mode_toggled: analytics.trackBatchModeToggled,
  result_copied: analytics.trackResultCopied,
  result_rerun: analytics.trackResultRerun,
  coach_dismissed: analytics.trackCoachDismissed,
  compare_modal_opened: analytics.trackCompareModalOpened,
  onboarding_video_watched: analytics.trackOnboardingVideoWatched,
  try_for_yourself_clicked: analytics.trackTryForYourselfClicked,
  stream_started: analytics.trackStreamStarted,
  stream_queued: analytics.trackStreamQueued,
  stream_live: analytics.trackStreamLive,
  stream_stopped: analytics.trackStreamStopped,
  stream_quota_warning: analytics.trackStreamQuotaWarning,
  stream_quota_exceeded: analytics.trackStreamQuotaExceeded,
  stream_error: analytics.trackStreamError,
  stream_camera_denied: analytics.trackStreamCameraDenied,
  stream_prompt_updated: analytics.trackStreamPromptUpdated,
  narrative_feedback: analytics.trackNarrativeFeedback,
  raw_api_tray_opened: analytics.trackRawApiTrayOpened,
  batch_analysis_submitted: analytics.trackBatchAnalysisSubmitted,
  upgrade_from_quota_wall: (p: { trigger: 'credits_exhausted' | 'warning_banner' | 'anon_trial_exhausted' | 'anon_trial_low' }) => analytics.trackUpgradeFromQuotaWall(p.trigger),
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const tracker: TelemetryTracker = {
  track(event, props) {
    try {
      const fn = EVENT_DISPATCH[event]
      if (fn) fn(props)
    } catch { /* analytics must never throw into product code */ }
  },
  trackError(error, context) {
    try { analytics.trackError(error, context) } catch { /* never throw */ }
  },
}

// ── Reporter: Sentry-backed ───────────────────────────────────────────────────

const reporter: TelemetryErrorReporter = {
  addBreadcrumb(crumb) {
    try { Sentry.addBreadcrumb(crumb) } catch { /* never throw */ }
  },
  captureMessage(message, opts) {
    try { Sentry.captureMessage(message, opts as Parameters<typeof Sentry.captureMessage>[1]) } catch { /* never throw */ }
  },
  captureException(error, opts) {
    try { Sentry.captureException(error, opts as Parameters<typeof Sentry.captureException>[1]) } catch { /* never throw */ }
  },
}

export const primateTelemetry: Telemetry = { tracker, reporter }
